import React, { useEffect } from "react";
import "@/kp-approved.css";
import { KP_PAGE_HTML } from "@/kpApprovedPage";

const TIER_PRICES = { "Paint & Sip": 55, "Sip & Release": 75, "Luxury Release": 100 };

function ApprovedPage() {
    useEffect(() => {
        const form = document.querySelector("#booking-form");
        const submitBtn = form.querySelector('button[type="submit"]');
        const result = document.querySelector("#booking-result");
        const draftArea = document.querySelector("#bk-draft");
        const openLink = document.querySelector("#bk-open");
        const copyBtn = document.querySelector("#bk-copy");
        const copyNote = document.querySelector("#bk-copy-note");
        const errorBox = document.querySelector("#bk-error");

        // JS initialized: enable the submit button (no-JS keeps it disabled)
        submitBtn.disabled = false;

        const clearDraft = () => {
            result.hidden = true;
            draftArea.value = "";
            openLink.removeAttribute("href");
            openLink.setAttribute("aria-disabled", "true");
            copyNote.hidden = true;
        };

        // any edit, reset, or new tier selection clears the old draft and email link
        form.addEventListener("input", clearDraft);
        form.addEventListener("change", clearDraft);
        form.addEventListener("reset", clearDraft);

        const tierLinks = Array.from(document.querySelectorAll("[data-tier]"));
        const onTierClick = (event) => {
            const select = document.querySelector("#bk-package");
            if (select) select.value = event.currentTarget.dataset.tier;
            clearDraft();
        };
        tierLinks.forEach((a) => a.addEventListener("click", onTierClick));

        // existing custom/private experience links keep their anchors and prefill the notes
        const expLinks = Array.from(document.querySelectorAll("[data-experience]"));
        const onExpClick = (event) => {
            const notes = document.querySelector("#bk-notes");
            if (notes && !notes.value.trim()) {
                notes.value = "Interested in: " + event.currentTarget.dataset.experience;
            }
            clearDraft();
        };
        expLinks.forEach((a) => a.addEventListener("click", onExpClick));

        const onSubmit = (event) => {
            event.preventDefault();
            errorBox.hidden = true;

            const nameInput = form.querySelector("#bk-name");
            if (!nameInput.value.trim()) {
                errorBox.textContent =
                    "Please add your full name — it can’t be blank or only spaces.";
                errorBox.hidden = false;
                nameInput.focus();
                return;
            }
            const guestsInput = form.querySelector("#bk-guests");
            const guests = Number(guestsInput.value);
            if (!Number.isInteger(guests) || guests < 1) {
                errorBox.textContent =
                    "Please add the number of guests as a whole number (1 or more).";
                errorBox.hidden = false;
                guestsInput.focus();
                return;
            }

            const pkg = form.querySelector("#bk-package").value;
            const price = TIER_PRICES[pkg];
            const email = form.querySelector("#bk-email").value.trim();
            const phone = form.querySelector("#bk-phone").value.trim();
            const date = form.querySelector("#bk-date").value;
            const theme = form.querySelector("#bk-theme").value;
            const notes = form.querySelector("#bk-notes").value.trim();

            const lines = [
                `Package: ${pkg} ($${price} per person)`,
                `Name: ${nameInput.value.trim()}`,
                `Email: ${email}`,
                `Phone: ${phone || "Not provided"}`,
                `Guests: ${guests}`,
                `Preferred date: ${date || "To be discussed"}`,
                `Monthly theme: ${theme || "No theme selected"}`,
                `Notes: ${notes || "None"}`,
                "",
                "Please confirm availability and next steps. I understand this is a request, not a confirmed reservation.",
            ];
            const draft = lines.join("\n");
            const subject = `Booking request: ${pkg} - $${price} per person`;
            const mailto =
                "mailto:krystal@kpspaintsiprelease.com?subject=" +
                encodeURIComponent(subject) +
                "&body=" +
                encodeURIComponent(draft);

            // repeated submits update this one result; nothing is sent automatically
            draftArea.value = draft;
            openLink.href = mailto;
            openLink.removeAttribute("aria-disabled");
            copyNote.hidden = true;
            result.hidden = false;
            result.scrollIntoView({ block: "nearest" });
        };
        form.addEventListener("submit", onSubmit);

        const onCopy = () => {
            const text = draftArea.value;
            const fallback = () => {
                draftArea.focus();
                draftArea.select();
                copyNote.textContent =
                    "Copy didn’t run automatically — the request is selected above: press Ctrl+C (or Cmd+C) to copy it.";
                copyNote.hidden = false;
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard
                    .writeText(text)
                    .then(() => {
                        copyNote.textContent =
                            "Copied. Your request is on your clipboard — paste it into your email.";
                        copyNote.hidden = false;
                    })
                    .catch(fallback);
            } else {
                fallback();
            }
        };
        copyBtn.addEventListener("click", onCopy);

        return () => {
            form.removeEventListener("input", clearDraft);
            form.removeEventListener("change", clearDraft);
            form.removeEventListener("reset", clearDraft);
            form.removeEventListener("submit", onSubmit);
            copyBtn.removeEventListener("click", onCopy);
            tierLinks.forEach((a) => a.removeEventListener("click", onTierClick));
            expLinks.forEach((a) => a.removeEventListener("click", onExpClick));
        };
    }, []);

    return <div dangerouslySetInnerHTML={{ __html: KP_PAGE_HTML }} />;
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 40, fontFamily: "Arial, sans-serif" }}>
                    <h1>KP’s Paint Sip &amp; Release Experience LLC</h1>
                    <p>
                        Something went wrong. Please refresh the page, email{" "}
                        <a href="mailto:krystal@kpspaintsiprelease.com">krystal@kpspaintsiprelease.com</a> or
                        call <a href="tel:+18175231302">817-523-1302</a>.
                    </p>
                </div>
            );
        }
        return this.props.children;
    }
}

function App() {
    return (
        <ErrorBoundary>
            <ApprovedPage />
        </ErrorBoundary>
    );
}

export default App;
